import { useState } from "react";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { PromoteTableModal } from "~/pages/manifold-nextgen/components/datasets/PromoteTableModal";
import usePersonalDatasetTables from "~/pages/manifold-nextgen/hooks/usePersonalDatasetTables";

const TableSkeleton = () => (
  <div role="status" aria-label="Loading tables" className="flex flex-col gap-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-16 w-full rounded-md" />
    ))}
  </div>
);

const TableRow = ({ table, onPromote }: { table: DatasetTableResponse; onPromote: () => void }) => (
  <div className="flex flex-col gap-1 border-b border-border py-4 last:border-0">
    <span className="text-sm font-semibold text-foreground">{table.name}</span>
    <p className="text-xs text-muted-foreground">
      {table.database_schema}
      {table.tableType && (
        <>
          {" · "}
          <span>{table.tableType === "Regular" ? "Table" : table.tableType}</span>
        </>
      )}
    </p>
    <p className="line-clamp-2 text-sm text-muted-foreground">
      {table.description || <span className="italic">No description</span>}
    </p>
    <div>
      <Button variant="outline" size="sm" onClick={onPromote}>
        Promote to dataset
      </Button>
    </div>
  </div>
);

export const PersonalDatasetTableList = () => {
  const { data: tables, isLoading, mutate } = usePersonalDatasetTables();
  const [promoteTarget, setPromoteTarget] = useState<DatasetTableResponse | null>(null);

  const showEmpty = !isLoading && (!tables || tables.length === 0);

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <TableSkeleton />
      ) : showEmpty ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No tables in your personal dataset.
        </p>
      ) : (
        <div>
          {tables?.map((table) => (
            <TableRow
              key={table.fullyQualifiedName}
              table={table}
              onPromote={() => setPromoteTarget(table)}
            />
          ))}
        </div>
      )}

      <PromoteTableModal
        table={promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onPromoted={() => mutate()}
      />
    </div>
  );
};
